import React from 'react';
import styled from '@emotion/styled';

type AvatarAppearanceProps = {
  avatarUrl?: string;
  onPreview: () => void;
  onChangeAvatar: () => void;
  whitelabelEnabled?: boolean;
  primaryColor?: string;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 600px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #333;
`;

const Description = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0 0 16px 0;
`;

const AvatarPreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
`;

const AvatarImage = styled.img`
  width: 200px;
  height: 200px;
  object-fit: cover;
  border-radius: 8px;
  background-color: #e0e0e0;
`;

const AvatarPlaceholder = styled.div`
  width: 200px;
  height: 200px;
  background-color: #e0e0e0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 14px;
  text-align: center;
  padding: 20px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 200px;
`;

const Button = styled.button<{ variant: 'primary' | 'secondary'; whitelabelEnabled?: boolean; primaryColor?: string }>`
  width: 240px;
  height: 40px;
  padding: 0 16px;
  border-radius: 5px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid;
  text-align: center;
  
  ${props => props.variant === 'primary' ? `
    background-color: ${props.whitelabelEnabled && props.primaryColor ? props.primaryColor : 'var(--sec-05)'};
    border-color: ${props.whitelabelEnabled && props.primaryColor ? props.primaryColor : 'var(--sec-05)'};
    color: white;
    
    &:hover {
      opacity: 0.9;
    }
  ` : `
    background-color: white;
    border-color: ${props.whitelabelEnabled && props.primaryColor ? props.primaryColor : 'var(--sec-05)'};
    color: ${props.whitelabelEnabled && props.primaryColor ? props.primaryColor : 'var(--sec-05)'};
    
    &:hover {
      background-color: ${props.whitelabelEnabled && props.primaryColor ? `${props.primaryColor}0a` : 'rgba(40, 170, 225, 0.05)'};
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export function AvatarAppearance({
  avatarUrl,
  onPreview,
  onChangeAvatar,
  whitelabelEnabled,
  primaryColor,
}: AvatarAppearanceProps) {
  return (
    <Container>
      <div>
        <SectionTitle>Choose Avatar Appearance</SectionTitle>
        <Description>Select an avatar to represent your segment in the interface</Description>
      </div>
      
      <AvatarPreviewContainer>
        {avatarUrl ? (
          <AvatarImage src={avatarUrl} alt="Avatar preview" />
        ) : (
          <AvatarPlaceholder>
            No avatar selected
            <br />
            Click "Change Avatar" to select one
          </AvatarPlaceholder>
        )}
        
        <ButtonContainer>
          <Button
            variant="primary"
            onClick={onPreview}
            disabled={!avatarUrl}
            whitelabelEnabled={whitelabelEnabled}
            primaryColor={primaryColor}>
            Preview
          </Button>
          <Button
            variant="secondary"
            onClick={onChangeAvatar}
            whitelabelEnabled={whitelabelEnabled}
            primaryColor={primaryColor}>
            Change Avatar
          </Button>
        </ButtonContainer>
      </AvatarPreviewContainer>
    </Container>
  );
}
